import React, { useState, useRef, useEffect, useCallback } from 'react';
import './Calculator.css';
import { ProductData } from './ProductData';
import logo from '../../assets/logo/aultralogo.jpg'

const Calculator = () => {
  const [selectedCategory, setSelectedCategory] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [carpetArea, setCarpetArea] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [calculation, setCalculation] = useState(null);
  const scrollContainerRef = useRef(null);

  const filteredProducts = ProductData.filter(
    product => product.categoryId === selectedCategory
  );

  // Set initial product when category changes
  useEffect(() => {
    if (filteredProducts.length > 0) {
      setSelectedProduct(filteredProducts[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  // Memoize performCalculation to avoid recreating on every render
  const performCalculation = useCallback(() => {
    if (!carpetArea || !selectedProduct) return;

    const carpet = parseFloat(carpetArea);
    const multiplier = selectedCategory === 1 ? 2.5 : 1.5;
    const paintableArea = carpet * multiplier;

    const coats = parseInt(selectedProduct.coats);
    const packaging = parseFloat(selectedProduct.packaging);
    const minCoverage = parseFloat(selectedProduct.CoverageAreamin);
    const maxCoverage = parseFloat(selectedProduct.CoverageAreamax);

    const paintMinRequired = ((paintableArea / minCoverage) * packaging).toFixed(2);
    const paintMaxRequired = ((paintableArea / maxCoverage) * packaging).toFixed(2);

    let unit = 'Ltr';
    if (selectedProduct.availablePackings.Kgs) {
      unit = 'Kgs';
    } else if (selectedProduct.availablePackings.ml) {
      unit = 'ml';
    }

    setCalculation({
      paintableArea: paintableArea.toFixed(2),
      coats,
      paintRequired: minCoverage === maxCoverage ? paintMinRequired : `${paintMaxRequired} - ${paintMinRequired}`,
      unit,
      showRange: minCoverage !== maxCoverage
    });

    setShowResults(true);
  }, [carpetArea, selectedProduct, selectedCategory]);

  // Auto-recalculate when product changes if results are already showing
  useEffect(() => {
    if (showResults && carpetArea && selectedProduct) {
      performCalculation();
    }
  }, [selectedProduct, showResults, carpetArea, performCalculation]);

  const handleCalculate = () => {
    performCalculation();
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
  };

  const getCategoryName = (id) => {
    switch(id) {
      case 1: return 'Interior';
      case 2: return 'Exterior';
      case 3: return 'Protectors';
      default: return '';
    }
  };

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      const scrollAmount = 200;
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="calculator-container">
      <div className="calculator-wrapper">
        {/* Left Section - Calculator */}
        <div className="calculator-form-section">
          <h1 className="calculator-title">Paint Calculator</h1>
          
          {/* Category Tabs */}
          <div className="calculator-tabs">
            <button
              className={`calculator-tab ${selectedCategory === 1 ? 'active' : ''}`}
              onClick={() => setSelectedCategory(1)}
            >
              Interior
            </button>
            <button
              className={`calculator-tab ${selectedCategory === 2 ? 'active' : ''}`}
              onClick={() => setSelectedCategory(2)}
            >
              Exterior
            </button>
            <button
              className={`calculator-tab ${selectedCategory === 3 ? 'active' : ''}`}
              onClick={() => setSelectedCategory(3)}
            >
              Protectors
            </button>
          </div>

          {/* Products Horizontal Scroll */}
          <div className="calculator-section">
            <h2 className="calculator-section-title-dark">Select Product</h2>
            <div className="calculator-products-wrapper">
              <button 
                className="calculator-scroll-btn calculator-scroll-left"
                onClick={() => scroll('left')}
                aria-label="Scroll left"
              >
                &#8249;
              </button>
              <div className="calculator-products-horizontal" ref={scrollContainerRef}>
                {filteredProducts.map((product) => (
                  <button
                    key={product.id}
                    className={`calculator-product-card ${selectedProduct?.id === product.id ? 'selected' : ''}`}
                    onClick={() => handleProductSelect(product)}
                  >
                    <div className="calculator-product-image">
                      <img src={product.image} alt={product.name} />
                      {selectedProduct?.id === product.id && (
                        <div className="calculator-product-check">✓</div>
                      )}
                    </div>
                    <span className="calculator-product-name">{product.name}</span>
                  </button>
                ))}
              </div>
              <button 
                className="calculator-scroll-btn calculator-scroll-right"
                onClick={() => scroll('right')}
                aria-label="Scroll right"
              >
                &#8250;
              </button>
            </div>
          </div>

          {/* Carpet Area Input with Calculate Button */}
          <div className="calculator-section">
            <h2 className="calculator-section-title-dark">Enter Carpet Area</h2>
            <div className="calculator-input-row">
              <input
                type="number"
                placeholder="Enter carpet area in Sq.Ft."
                value={carpetArea}
                onChange={(e) => setCarpetArea(e.target.value)}
                className="calculator-input-new"
                min="0"
                onWheel={(e) => e.target.blur()}
              />
              <button className="calculator-estimate-btn" onClick={handleCalculate}>
                Calculate
              </button>
            </div>
          </div>
        </div>

        {/* Right Section - Results or Logo */}
        <div className={`calculator-results-section ${showResults ? 'show-results' : ''}`}>
          {!showResults ? (
            <div className="calculator-logo-container">
              <div className="calculator-logo">
                <img src={logo} alt="Logo" />
              </div>
              <p className="calculator-logo-text">Enter details and calculate to see results</p>
            </div>
          ) : (
            <div className="calculator-results-content">
              <h2 className="calculator-results-title">Paint Estimate</h2>
              
              <div className="calculator-result-card">
                <h3 className="calculator-result-type">{selectedProduct?.name}</h3>
                <p className="calculator-result-category">{getCategoryName(selectedCategory)}</p>
                
                <div className="calculator-result-details">
                  <div className="calculator-result-item">
                    <span className="calculator-result-label">Carpet Area:</span>
                    <span className="calculator-result-value">{carpetArea} sq.ft.</span>
                  </div>
                  
                  <div className="calculator-result-item">
                    <span className="calculator-result-label">Paintable Area:</span>
                    <span className="calculator-result-value">{calculation?.paintableArea} sq.ft.</span>
                  </div>
                  
                  <div className="calculator-result-item">
                    <span className="calculator-result-label">Number of Coats:</span>
                    <span className="calculator-result-value">{calculation?.coats}</span>
                  </div>
                  
                  <div className="calculator-result-item calculator-highlight">
                    <span className="calculator-result-label">Paint Required:</span>
                    <span className="calculator-result-value-large">
                      {calculation?.paintRequired} {calculation?.unit}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Calculator;
